/**
 * Utility functions for handling YouTube URLs
 */

/**
 * Cleans YouTube URLs by removing problematic parameters that can cause errors
 * @param url - The YouTube URL to clean
 * @returns The cleaned YouTube URL
 */
export const cleanYouTubeUrl = (url: string): string => {
  try {
    const urlObj = new URL(url.trim());
    const hostname = urlObj.hostname.toLowerCase();
    
    // Remove problematic parameters that can cause errors
    const paramsToRemove = ['start_radio', 'list', 'feature', 'app', 'si', 'ab_channel'];
    paramsToRemove.forEach(param => {
      urlObj.searchParams.delete(param);
    });
    
    // Ensure we have the essential video ID parameter
    if (!urlObj.searchParams.has('v')) {
      // If it's a youtu.be URL, convert it to youtube.com format
      if (urlObj.hostname === 'youtu.be') {
        const videoId = urlObj.pathname.slice(1); // Remove leading slash
        urlObj.hostname = 'www.youtube.com';
        urlObj.pathname = '/watch';
        urlObj.searchParams.set('v', videoId);
      }
      // If it's a youtube.com/shorts/ URL, convert it to youtube.com/watch format
      else if (urlObj.pathname.startsWith('/shorts/')) {
        const videoId = urlObj.pathname.split('/shorts/')[1];
        urlObj.pathname = '/watch';
        urlObj.searchParams.set('v', videoId);
      }
    }

    // Canonicalize regular YouTube watch URLs. URL parsing preserves a trailing
    // slash in "/watch/?v=...", while validation and the player expect
    // "/watch?v=...". Using one hostname also makes www optional for users.
    if (hostname === 'youtube.com' || hostname === 'www.youtube.com') {
      urlObj.hostname = 'www.youtube.com';
      if (urlObj.pathname === '/watch/') {
        urlObj.pathname = '/watch';
      }
    }
    
    return urlObj.toString();
  } catch (error) {
    // If URL parsing fails, return the original URL
    console.warn('Failed to parse YouTube URL for cleaning:', error);
    return url;
  }
};

/**
 * Validates if a URL is a valid YouTube URL
 * @param url - The URL to validate
 * @returns True if the URL is a valid YouTube URL
 */
export const validateYouTubeUrl = (url: string): boolean => {
  // Clean the URL first, then validate
  const cleanedUrl = cleanYouTubeUrl(url);
  const youtubeRegex = /^(?:https?:\/\/)?(?:www\.)?(?:youtube\.com\/(?:watch\?v=|shorts\/)|youtu\.be\/)([a-zA-Z0-9_-]{11})/;
  return youtubeRegex.test(cleanedUrl);
};

/**
 * Extracts the video ID from a YouTube URL
 * @param url - The YouTube URL
 * @returns The video ID or null if not found
 */
export const extractVideoId = (url: string): string | null => {
  // Clean the URL first, then extract video ID
  const cleanedUrl = cleanYouTubeUrl(url);
  const youtubeRegex = /^(?:https?:\/\/)?(?:www\.)?(?:youtube\.com\/(?:watch\?v=|shorts\/)|youtu\.be\/)([a-zA-Z0-9_-]{11})/;
  const match = cleanedUrl.match(youtubeRegex);
  return match ? match[1] : null;
};
